import { Clock, AlertTriangle, Eye, CheckCircle, DollarSign, AlertCircle, Layers } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCreatorPortal } from '../../contexts/CreatorPortalContext';
import { formatEuro } from '../../utils/currency';
import { deriveInfluencerInvoiceStatus } from '../../utils/paymentCalculations';
import type { CreatorCampaignData } from '../../services/creatorPortalService';

interface Props {
  campaign: CreatorCampaignData;
  compact?: boolean;
}

function getPlatformAbbr(platform: string): string {
  const map: Record<string, string> = {
    Instagram: 'IG', TikTok: 'TT', YouTube: 'YT', Facebook: 'FB',
    X: 'X', Podcast: 'POD', 'Paid Media': 'AD', Event: 'EVT', Other: 'OT',
  };
  return map[platform] || platform.slice(0, 2).toUpperCase();
}

function getPlatformPillStyle(platform: string): string {
  const map: Record<string, string> = {
    Instagram: 'bg-pink-50 text-pink-600',
    TikTok: 'bg-gray-100 text-gray-700',
    YouTube: 'bg-red-50 text-red-600',
    Facebook: 'bg-blue-50 text-blue-600',
    X: 'bg-gray-100 text-gray-700',
    Podcast: 'bg-violet-50 text-violet-600',
    'Paid Media': 'bg-cyan-50 text-cyan-600',
    Event: 'bg-teal-50 text-teal-600',
  };
  return map[platform] || 'bg-gray-50 text-gray-500';
}

function getStatusAccent(status: string) {
  const s = status.toLowerCase();
  if (s === 'in progress' || s === 'production') return 'border-l-blue-500';
  if (s === 'billing' || s === 'faturação') return 'border-l-amber-500';
  if (s === 'completed' || s === 'liquidado' || s === 'paid') return 'border-l-green-500';
  if (s === 'consultation' || s === 'consulta') return 'border-l-gray-400';
  return 'border-l-gray-300';
}

function getStatusBadge(status: string) {
  const s = status.toLowerCase();
  if (s === 'completed' || s === 'liquidado' || s === 'paid')
    return 'bg-emerald-50 text-emerald-700';
  if (s === 'billing' || s === 'faturação')
    return 'bg-amber-50 text-amber-700';
  if (s === 'in progress' || s === 'em andamento')
    return 'bg-blue-50 text-blue-700';
  if (s === 'consultation' || s === 'consulta')
    return 'bg-gray-100 text-gray-600';
  return 'bg-gray-100 text-gray-600';
}

function getPaymentInfo(campaign: CreatorCampaignData) {
  if (deriveInfluencerInvoiceStatus(campaign) === 'Paid')
    return { labelKey: 'creatorsHub.payment.paid', color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle };
  if (campaign.company_invoice_status === 'Paid')
    return { labelKey: 'creatorsHub.payment.ready', color: 'text-emerald-600', bg: 'bg-emerald-50', icon: DollarSign };
  if (campaign.company_invoice_status === 'Issued' || campaign.company_invoice_status === 'Overdue')
    return { labelKey: 'creatorsHub.payment.billing', color: 'text-amber-600', bg: 'bg-amber-50', icon: Clock };
  if (campaign.status === 'In Progress')
    return { labelKey: 'creatorsHub.payment.inProgress', color: 'text-blue-600', bg: 'bg-blue-50', icon: Clock };
  return { labelKey: 'creatorsHub.payment.pending', color: 'text-gray-500', bg: 'bg-gray-50', icon: AlertCircle };
}

export function CreatorCampaignCard({ campaign, compact }: Props) {
  const { t } = useLanguage();
  const { setSelectedCampaignId } = useCreatorPortal();
  const payment = getPaymentInfo(campaign);
  const PaymentIcon = payment.icon;

  const deliverableProgress = campaign.deliverables_count > 0
    ? (campaign.deliverables_published / campaign.deliverables_count) * 100
    : 0;

  const daysUntilDeadline = campaign.next_deadline
    ? Math.ceil((new Date(campaign.next_deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <button
      onClick={() => setSelectedCampaignId(campaign.id)}
      className={`
        w-full text-left bg-white rounded-xl border border-gray-100 border-l-4 ${getStatusAccent(campaign.status)}
        shadow-sm hover:shadow-md transition-all duration-200 group
        focus:outline-none focus:ring-2 focus:ring-accent-200 focus:ring-offset-1
      `}
    >
      <div className={compact ? 'p-4' : 'p-5'}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
              {campaign.name}
            </h3>
            <p className="text-xs text-gray-500 truncate mt-0.5">{campaign.brand}</p>
          </div>
          <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-xl whitespace-nowrap ${getStatusBadge(campaign.status)}`}>
            {campaign.status}
          </span>
        </div>

        {campaign.deliverables_count > 0 && (
          <div className="mb-3">
            <div className="flex justify-between text-[11px] text-gray-500 mb-1">
              <span>{t('creatorsHub.deliverables')}</span>
              <span className="font-medium text-gray-700">
                {campaign.deliverables_published}/{campaign.deliverables_count}
              </span>
            </div>
            <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                style={{ width: `${deliverableProgress}%` }}
              />
            </div>
            {Object.keys(campaign.platform_summary).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">
                {Object.entries(campaign.platform_summary)
                  .sort(([, a], [, b]) => b - a)
                  .slice(0, 4)
                  .map(([platform, count]) => (
                    <span
                      key={platform}
                      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-semibold ${getPlatformPillStyle(platform)}`}
                    >
                      {getPlatformAbbr(platform)}{count > 1 ? ` x${count}` : ''}
                    </span>
                  ))}
                {Object.keys(campaign.platform_summary).length > 4 && (
                  <span className="text-[9px] text-gray-400 px-1 py-0.5">
                    +{Object.keys(campaign.platform_summary).length - 4}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <PaymentIcon className={`w-3.5 h-3.5 ${payment.color}`} />
            <span className={`text-[11px] font-medium ${payment.color} px-1.5 py-0.5 rounded ${payment.bg}`}>
              {t(payment.labelKey)}
            </span>
            {campaign.is_installment_campaign && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded flex items-center gap-0.5 ${
                campaign.installment_paid === campaign.installment_total
                  ? 'bg-green-50 text-green-600'
                  : campaign.installment_paid > 0
                  ? 'bg-blue-50 text-blue-600'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                <Layers className="w-3 h-3" />
                {campaign.installment_paid}/{campaign.installment_total}
              </span>
            )}
          </div>
          {campaign.deliverables_overdue > 0 && (
            <div className="flex items-center gap-1 text-red-500">
              <AlertTriangle className="w-3 h-3" />
              <span className="text-[10px] font-medium">
                {campaign.deliverables_overdue} {t('creatorsHub.overdue')}
              </span>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 pt-3 flex items-end justify-between">
          <div>
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">{t('creatorsHub.netEarnings')}</p>
            <p className="text-base font-bold text-gray-900">{formatEuro(campaign.net_earnings)}</p>
          </div>
          <div className="flex items-center gap-2">
            {daysUntilDeadline !== null && daysUntilDeadline <= 7 && daysUntilDeadline >= 0 && (
              <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                {daysUntilDeadline === 0 ? t('creatorsHub.dueToday') : `${daysUntilDeadline}d`}
              </span>
            )}
            <div className="w-7 h-7 rounded-lg bg-gray-50 group-hover:bg-blue-50 flex items-center justify-center transition-colors">
              <Eye className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 transition-colors" />
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}
